/**
 * Data export screen — D.L. 144/2024 Right to Portability.
 *
 * Real implementation (not mailto): calls `complianceService.requestDataExport()`
 * which invokes the `export-user-data` edge function. The response is the
 * aggregated JSON envelope of every record keyed to the caller's
 * profile_id. We hand it to the platform — on web it triggers a Blob
 * download; on native it writes to the cache and opens the system share
 * sheet so the user can save it to Files / Drive / Mail.
 *
 * The audit trail is written server-side by the edge function (see
 * `audit_logs` table — `data_export.fulfilled` action).
 */
import { useCallback, useState } from 'react'
import { View, Text, Linking, Platform } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useTranslation } from 'react-i18next'
import { Download, Clock, FileText, CheckCircle2 } from '@/components/ui/icons'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Button } from '@/components/ui/Button'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { complianceService } from '@/lib/services/compliance.service'
import { toast } from '@/lib/toast'
import { errorMessage, captureError } from '@/lib/utils/observability'

export default function DataExportScreen() {
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [pending, setPending] = useState(false)
  const [completedAt, setCompletedAt] = useState<string | null>(null)

  const downloadJson = useCallback(
    async (envelope: Record<string, unknown>) => {
      const json = JSON.stringify(envelope, null, 2)
      const filename = `tago-data-export-${new Date().toISOString().split('T')[0]}.json`

      if (Platform.OS === 'web') {
        // Browser download via Blob URL.
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        return
      }

      // Native: write to cache, then open share sheet via expo-sharing if
      // available. expo-sharing isn't a hard dep — degrade gracefully if
      // missing by leaving the file in cache and informing the user.
      //
      // expo-file-system v54 reorganised the API: legacy `cacheDirectory` /
      // `writeAsStringAsync` / `EncodingType` moved to /legacy. The new API
      // uses `Paths.cache` + a class-based `File` helper that joins URIs
      // for us (no manual slash concatenation).
      const FS = await import('expo-file-system')
      const file = new FS.File(FS.Paths.cache, filename)
      file.create({ overwrite: true })
      file.write(json) // UTF-8 is the default for string payloads
      try {
        // Dynamic require so the bundler doesn't fail when expo-sharing
        // isn't installed yet (it's an optional native dep — adding to
        // package.json now would force a new AAB just for this surface).
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const Sharing = require('expo-sharing') as {
          isAvailableAsync: () => Promise<boolean>
          shareAsync: (
            path: string,
            opts: { mimeType: string; dialogTitle?: string },
          ) => Promise<void>
        }
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(file.uri, {
            mimeType: 'application/json',
            dialogTitle: filename,
          })
          return
        }
      } catch {
        /* expo-sharing not available — fall through */
      }
      toast.info(t('data_export.saved_to_cache'))
    },
    [t],
  )

  const handleRequestExport = useCallback(async () => {
    setPending(true)
    try {
      const envelope = await complianceService.requestDataExport()
      await downloadJson(envelope)
      setCompletedAt(new Date().toISOString())
      toast.success(t('data_export.success'))
    } catch (err) {
      if (__DEV__) console.error('[DataExport]', err)
      captureError(err, { screen: 'data-export', action: 'request' })
      toast.error(errorMessage(err, t('data_export.error')))
    } finally {
      setPending(false)
    }
  }, [downloadJson, t])

  const handleEmailFallback = useCallback(() => {
    const subject = encodeURIComponent('Data Export Request / Solicitud de Exportación de Datos')
    const body = encodeURIComponent(
      'I would like to request an export of my personal data in accordance with ARCO rights.\n\n' +
        'Solicito la exportación de mis datos personales conforme a los derechos ARCO.\n\n---\nAccount email: \nFull name: \n',
    )
    void Linking.openURL(`mailto:privacy@tago.rent?subject=${subject}&body=${body}`)
  }, [])

  return (
    <ScreenContainer scroll={false}>
      <ScreenHeader title={t('data_export.title')} backTestID="data-export.back" />
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.lg }}
      >
        {/* One content gutter for the whole page (SPACING.lg, the same one
            ScreenHeader uses). The page used to run on four different left
            edges — hero centred, card text at 32, button at 16, legal note
            centred — so nothing established a reading edge. Centring is now
            reserved for the icon and the hero paragraph. */}
        <View
          style={{ alignItems: 'center', marginTop: SPACING.xl, paddingHorizontal: SPACING.lg }}
        >
          <View
            style={{
              width: 80,
              height: 80,
              borderRadius: 40,
              backgroundColor: colors.surface,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: SPACING.lg,
              ...WEB_SHADOWS.sm,
            }}
          >
            <FileText size={36} color={colors.textMuted} strokeWidth={1.3} />
          </View>

          <Text
            style={{
              fontSize: 16,
              color: colors.textPrimary,
              fontFamily: FONTS.body,
              textAlign: 'center',
              lineHeight: 24,
              maxWidth: 340,
            }}
          >
            {t('data_export.description')}
          </Text>
        </View>

        <View
          style={{
            marginHorizontal: SPACING.lg,
            marginTop: SPACING.xl,
            backgroundColor: colors.surface,
            borderRadius: RADIUS.lg,
            padding: SPACING.md,
            ...WEB_SHADOWS.sm,
          }}
        >
          <Text
            style={{
              fontSize: 14,
              color: colors.textPrimary,
              fontFamily: FONTS.bodyBold,
              marginBottom: SPACING.sm,
            }}
          >
            {t('data_export.arco_title')}
          </Text>
          <Text
            style={{
              fontSize: 14,
              color: colors.textSecondary,
              fontFamily: FONTS.body,
              lineHeight: 20,
            }}
          >
            {t('data_export.arco_body')}
          </Text>
        </View>

        <View style={{ paddingHorizontal: SPACING.lg, marginTop: SPACING.xl }}>
          <Button
            title={
              pending
                ? t('data_export.requesting')
                : completedAt
                  ? t('data_export.regenerate')
                  : t('data_export.request_button')
            }
            onPress={handleRequestExport}
            variant="primary"
            size="lg"
            loading={pending}
            disabled={pending}
            icon={<Download size={20} color="#FFFFFF" strokeWidth={2} />}
            testID="data-export.request-button"
          />
        </View>

        {completedAt ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              paddingHorizontal: SPACING.lg,
              marginTop: SPACING.md,
              gap: SPACING.sm,
            }}
          >
            <View style={{ marginTop: 1 }}>
              <CheckCircle2 size={16} color={COLORS.purple.DEFAULT} strokeWidth={2} />
            </View>
            <Text
              style={{
                flex: 1,
                fontSize: 13,
                color: colors.textSecondary,
                fontFamily: FONTS.body,
                lineHeight: 18,
              }}
            >
              {t('data_export.completed_note')}
            </Text>
          </View>
        ) : null}

        <View style={{ paddingHorizontal: SPACING.lg, marginTop: SPACING.lg }}>
          <Text
            style={{
              fontSize: 12,
              color: colors.textMuted,
              fontFamily: FONTS.body,
              lineHeight: 16,
            }}
          >
            {t('data_export.email_fallback_intro')}{' '}
            {/* Same recipe as every other inline link in the app (see
                components/auth/ConsentForm): the accent coral, not the purple
                that made this read like a default HTML anchor. */}
            <Text
              accessibilityRole="link"
              style={{
                fontFamily: FONTS.body,
                color: colors.textAccent,
                textDecorationLine: 'underline',
              }}
              onPress={handleEmailFallback}
            >
              privacy@tago.rent
            </Text>
          </Text>
        </View>

        {/* Five lines of text: a vertically centred icon lands beside line 3,
            anchored to nothing. Top-aligned it reads as the note's marker. */}
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            paddingHorizontal: SPACING.lg,
            marginTop: SPACING.lg,
            gap: SPACING.sm,
          }}
        >
          <View style={{ marginTop: 2 }}>
            <Clock size={16} color={colors.textMuted} strokeWidth={1.8} />
          </View>
          <Text
            style={{
              flex: 1,
              fontSize: 13,
              color: colors.textMuted,
              fontFamily: FONTS.body,
              lineHeight: 18,
            }}
          >
            {t('data_export.processing_note')}
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  )
}
